// Sign-out, A-06 and the access-error redirects (Frontend §3.2 steps 7–8, §5.1, §5.2). Each staff app creates one
// session and passes its handlers to `configureApi`.
import type { ApiError } from '@pa/api'
import type { QueryClient } from '@tanstack/react-query'
import { meQueryKey } from '../me/me'
import { signInHref } from './redirect'
import type { AuthStore } from './store'

/** The bits of the TanStack router the session needs (any app's router). */
export interface SessionRouter {
  navigate(options: { href: string; replace?: boolean }): Promise<unknown>
  invalidate(): Promise<unknown>
  state: { location: { href: string } }
}

export interface StaffSession {
  /** A-05: Firebase sign-out, query cache cleared, then A-01 (or `href`). */
  signOut(href?: string): Promise<void>
  /** `configureApi.onAuthFailure`: A-06 → A-01 "Please sign in again", back to this page afterwards. */
  onAuthFailure(error: ApiError): void
  /** `configureApi.onAccessError`: re-read `/v1/me` and let the guards redirect. */
  onAccessError(error: ApiError): void
}

export function createStaffSession(deps: {
  auth: AuthStore
  queryClient: QueryClient
  getRouter: () => SessionRouter
  /**
   * Runs before the Firebase sign-out, best effort (task 13): the clinic app ends an open impersonation with #36
   * `reason: sign_out`, the admin app with #58 `?reason=sign_out` (Frontend §5.1, §6.4; T-A05-02).
   */
  beforeSignOut?: () => Promise<void>
  /** An app's own handling of an access error (the clinic app's `impersonation_ended`); true = handled. */
  onAccessError?: (error: ApiError) => boolean
}): StaffSession {
  const { auth, queryClient, getRouter } = deps
  let signingOut = false
  const go = (href: string) => getRouter().navigate({ href, replace: true })

  async function signOut(href = '/sign-in') {
    signingOut = true
    try {
      await deps.beforeSignOut?.()
    } catch {
      // Best effort: the sign-out goes ahead (an impersonation also ends at its 60-minute limit).
    }
    try {
      await auth.signOut()
    } catch {
      // Already signed out (or Firebase unreachable): the local session ends either way.
    } finally {
      signingOut = false
    }
    // Leave the staff page first, so its queries don't come back into the cache, then forget everything.
    await go(href)
    queryClient.clear()
  }

  // Cross-tab: another tab signed out (`onIdTokenChanged` → null) → this tab goes to A-01 too.
  let previous = auth.status
  auth.subscribe(() => {
    const now = auth.status
    if (previous === 'signedIn' && now === 'signedOut' && !signingOut && !auth.signingOutQuietly) {
      void go('/sign-in').then(() => {
        queryClient.clear()
      })
    }
    previous = now
  })

  return {
    signOut,
    onAuthFailure() {
      // During A-01 itself, a 401 from /v1/me is the generic sign-in message, not A-06.
      if (auth.signingIn) return
      const current = getRouter().state.location.href
      void signOut(signInHref({ reason: 'session-expired', redirect: current }))
    },
    onAccessError(error) {
      // Drop the cached `/v1/me`, so the guards read the server's current answer, not the one that let us in.
      queryClient.removeQueries({ queryKey: meQueryKey() })
      if (deps.onAccessError?.(error)) return
      if (error.code === 'password_change_required') void go('/set-password')
      else if (error.code === 'no_access') void go('/no-access')
      else void getRouter().invalidate()
    },
  }
}
